import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Tooltip, Typography, Tab, Tabs,
    InputAdornment, Grid,
    IconButton,
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import GroupIcon from '@mui/icons-material/Group'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApiGetReferralSummary } from '../../apiService/Core/Apicall'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useDateRange } from '../../hooks/useDateRange'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'

interface ReferredUser {
    _id: string
    firstName: string
    lastName: string
    email: string
    isOnbordingFlowCompleted: boolean
    createdAt: string
}

interface ReferralDeposit {
    _id: string
    transactionId: string
    userId: string | { _id: string; firstName: string; lastName: string; email: string }
    amount: number
    netAmount: number
    currency: string
    status: string
    createdAt: string
}

interface Pagination {
    page: number
    limit: number
    total: number
    pages: number
}

const formatAmount = (amount: number) =>
    amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatDate = (date: string) =>
    new Date(date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

const formatTime = (date: string) =>
    new Date(date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

const getUserInfo = (userId: ReferralDeposit['userId']) => {
    if (typeof userId === 'object' && userId !== null) {
        return { name: `${userId.firstName} ${userId.lastName}`, email: userId.email }
    }
    return { name: '—', email: '—' }
}

const statusMap: Record<string, { color: string; bg: string; border: string }> = {
    COMPLETED: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
    PENDING: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f650' },
    PROCESSING: { color: '#f59e0b', bg: '#f59e0b18', border: '#f59e0b50' },
    FAILED: { color: '#ef4444', bg: '#ef444418', border: '#ef444450' },
    CANCELLED: { color: '#9ca3af', bg: '#9ca3af18', border: '#9ca3af50' },
}

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
    const s = statusMap[status] || { color: '#9a9a9a', bg: '#9a9a9a18', border: '#9a9a9a50' }
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.5, py: 0.4, borderRadius: '6px', fontSize: '11px', fontWeight: 700,
            color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}`,
            letterSpacing: '0.03em', whiteSpace: 'nowrap',
        }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            {status}
        </Box>
    )
}

const StatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333',
        borderRadius: '14px',
        p: 2.5,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.25s',
        '&:hover': { borderColor: color + '60' },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">{label}</Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
        {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)

const thStyle = {
    backgroundColor: '#241815',
    color: '#ff6a3d',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    py: 1.5,
    borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
}

const tdStyle = {
    color: '#c0c0c0',
    py: 1.4,
    fontSize: 13,
    whiteSpace: 'nowrap' as const,
}

const trHover = {
    transition: 'background 0.15s',
    '&:hover': { background: '#ff6a3d08' },
}

const searchFieldStyle = {
    width: 280,
    background: 'transparent',
    borderRadius: '7px',
    border: '1px solid #898989',
    transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: 13 },
    '& input::placeholder': { color: '#555', opacity: 1 },
}

const paginationStyle = {
    color: '#6a6a6a', borderTop: '1px solid #252525',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
    '& .MuiSelect-select': { color: '#6a6a6a' },
    '& .MuiSelect-icon': { color: '#6a6a6a' },
    '& .MuiIconButton-root': { color: '#6a6a6a' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
}

const ReferralSummary: React.FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const userId = location.state?.userId

    const [tab, setTab] = useState(0)
    const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'pending'>('all')

    const [users, setUsers] = useState<ReferredUser[]>([])
    const [deposits, setDeposits] = useState<ReferralDeposit[]>([])
    const [depositsTotal, setDepositsTotal] = useState(0)
    const [counts, setCounts] = useState({ total: 0, completed: 0, pending: 0 })
    const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 10, total: 0, pages: 1 })

    const [search, setSearch] = useState('')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [errorMsg, setErrorMsg] = useState('')
    const {
        range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
    } = useDateRange('referral-summary', 'all')

    const fetchSummary = useCallback(async () => {
        if (!userId) return
        try {
            const dateQuery = buildQuery()
            if (dateQuery === null) return
            const params = new URLSearchParams()
            params.append('status', statusFilter)
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))
            if (dateQuery) {
                new URLSearchParams(dateQuery).forEach((value, key) => params.append(key, value))
            }

            const res = await ApiGetReferralSummary(userId, `?${params.toString()}`)
            if (res?.status) {
                setUsers(res.data?.referredUsers || [])
                setDeposits(res.data?.referralDeposits || [])
                setDepositsTotal(res.data?.referralDepositsTotal || 0)
                setCounts({
                    total: res.data?.totalReferred ?? 0,
                    completed: res.data?.completedCount ?? 0,
                    pending: res.data?.pendingCount ?? 0,
                })
                setPagination(res.data?.pagination || { page: 1, limit: rowsPerPage, total: 0, pages: 1 })
            }
        } catch (error: any) {
            setErrorMsg(error?.message || error?.response?.data?.message || 'Failed to fetch referral summary')
        }
    }, [userId, statusFilter, page, rowsPerPage, buildQuery])

    useEffect(() => {
        fetchSummary()
    }, [fetchSummary])

    useEffect(() => {
        setPage(0)
    }, [tab, statusFilter, range, customStart, customEnd])

    const filteredUsers = search
        ? users.filter(u =>
            `${u.firstName} ${u.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
            u.email?.toLowerCase().includes(search.toLowerCase())
        )
        : users

    const filteredDeposits = search
        ? deposits.filter(d => {
            const info = getUserInfo(d.userId)
            return info.name.toLowerCase().includes(search.toLowerCase()) ||
                info.email.toLowerCase().includes(search.toLowerCase())
        })
        : deposits

    return (
        <div className="common-layout flex-column">
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton
                        sx={{ ':hover': { backgroundColor: '#ffffff1A' } }}
                        onClick={() => navigate(-1)}
                    >
                        <ArrowBackIcon sx={{ color: '#ffffff' }} />
                    </IconButton>
                    <Typography className="fs-24 text-white fw-medium" sx={{ lineHeight: 1.1 }}>
                        Referral Summary
                    </Typography>
                </Box>
            </Box>

            <Grid container spacing={2} mb={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Total Referred" value={String(counts.total)}
                        sub="All-time referrals" color="#a78bfa"
                        icon={<GroupIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Onboarded" value={String(counts.completed)}
                        sub="Completed onboarding" color="#10b981"
                        icon={<CheckCircleOutlineIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Pending" value={String(counts.pending)}
                        sub="Awaiting onboarding" color="#f59e0b"
                        icon={<HourglassEmptyIcon fontSize="small" />} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard label="Referral Deposits" value={`$${formatAmount(depositsTotal)}`}
                        sub={`${deposits.length} transactions`} color="#3b82f6"
                        icon={<PaidOutlinedIcon fontSize="small" />} />
                </Grid>
            </Grid>

            <DateRangeFilter
                range={range}
                onRangeChange={handleRangeChange}
                customStart={customStart}
                onCustomStartChange={setCustomStart}
                customEnd={customEnd}
                onCustomEndChange={setCustomEnd}
            />

            <Box className="bg-dark-color border-10 p-0 mt-2" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: '16px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            sx={{
                                minHeight: 40,
                                background: '#1e1e1e',
                                borderRadius: '10px',
                                p: 0.5,
                                border: '1px solid #333',
                                '& .MuiTabs-indicator': { display: 'none' },
                                '& .MuiTab-root': {
                                    minHeight: 36, borderRadius: '8px', fontSize: 13, fontWeight: 600,
                                    color: '#6f6f6f', textTransform: 'none', px: 3, py: 0.5,
                                    transition: 'all 0.2s',
                                },
                                '& .Mui-selected': {
                                    color: '#fff !important',
                                    background: 'linear-gradient(135deg, #f2674a, #d94f35)',
                                    boxShadow: '0 2px 8px rgba(242,103,74,0.4)',
                                },
                            }}
                        >
                            <Tab label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GroupIcon sx={{ fontSize: 15 }} />
                                    Referred Users
                                    {counts.total > 0 && (
                                        <Box sx={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', px: 0.8, fontSize: 10, lineHeight: '18px' }}>
                                            {counts.total}
                                        </Box>
                                    )}
                                </Box>
                            } />
                            <Tab label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PaidOutlinedIcon sx={{ fontSize: 15 }} />
                                    Referral Deposits
                                    {deposits.length > 0 && (
                                        <Box sx={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', px: 0.8, fontSize: 10, lineHeight: '18px' }}>
                                            {deposits.length}
                                        </Box>
                                    )}
                                </Box>
                            } />
                        </Tabs>

                        {tab === 0 && (
                            <Tabs
                                value={statusFilter}
                                onChange={(_, v) => setStatusFilter(v)}
                                sx={{
                                    minHeight: 40,
                                    background: '#1e1e1e',
                                    borderRadius: '10px',
                                    p: 0.5,
                                    border: '1px solid #333',
                                    '& .MuiTabs-indicator': { display: 'none' },
                                    '& .MuiTab-root': {
                                        minHeight: 36, borderRadius: '8px', fontSize: 13, fontWeight: 600,
                                        color: '#6f6f6f', textTransform: 'none', px: 2.5, py: 0.5,
                                        transition: 'all 0.2s',
                                    },
                                    '& .Mui-selected': {
                                        color: '#fff !important',
                                        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                        boxShadow: '0 2px 8px rgba(59,130,246,0.4)',
                                    },
                                }}
                            >
                                <Tab label="All" value="all" />
                                <Tab label="Completed" value="completed" />
                                <Tab label="Pending" value="pending" />
                            </Tabs>
                        )}
                    </Box>

                    <TextField
                        placeholder="Search by name or email"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        sx={searchFieldStyle}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon className="color-primary" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {tab === 0 && (
                    <TableContainer sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                            <TableHead>
                                <TableRow>
                                    {['#', 'Name', 'Email', 'Referred On', 'Status'].map(h => (
                                        <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.length > 0 ? filteredUsers.map((u, i) => (
                                    <TableRow key={u._id} sx={{ ...trHover }}
                                    >
                                        <TableCell sx={tdStyle}>{page * rowsPerPage + i + 1}</TableCell>
                                        <TableCell sx={{ ...tdStyle, cursor: 'pointer' }}
                                            onClick={() => navigate('/admin/user-activity', {
                                                state: { userId: u._id, userName: `${u.firstName} ${u.lastName}` }
                                            })}
                                        >
                                            <Typography fontSize={13} fontWeight={600} color="#e0e0e0">
                                                {u.firstName} {u.lastName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>{u.email}</TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} color="#e0e0e0">{formatDate(u.createdAt)}</Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{formatTime(u.createdAt)}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <StatusPill status={u.isOnbordingFlowCompleted ? 'COMPLETED' : 'PENDING'} />
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                            <GroupIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                            <Typography color="#4a4a4a" fontSize={13}>
                                                No referred users found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {tab === 1 && (
                    <TableContainer sx={{ maxHeight: '60vh' }}>
                        <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                            <TableHead>
                                <TableRow>
                                    {['#', 'Transaction ID', 'Date & Time', 'Amount', 'Status'].map(h => (
                                        <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDeposits.length > 0 ? filteredDeposits.map((tx, i) => (
                                    <TableRow key={tx._id} sx={trHover}>
                                        <TableCell sx={tdStyle}>{i + 1}</TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Tooltip title={tx.transactionId} arrow>
                                                <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {tx.transactionId}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={13} color="#e0e0e0">{formatDate(tx.createdAt)}</Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{formatTime(tx.createdAt)}</Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}>
                                            <Typography fontSize={14} fontWeight={700} color="#10b981">
                                                +{tx.currency === 'AUD' ? 'A$' : '$'}{formatAmount(tx.amount)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={tdStyle}><StatusPill status={tx.status} /></TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                            <PaidOutlinedIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                            <Typography color="#4a4a4a" fontSize={13}>
                                                No referral deposits found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {tab === 0 && (
                    <TablePagination
                        component="div"
                        count={pagination.total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        showFirstButton showLastButton
                        sx={paginationStyle}
                    />
                )}
            </Box>

            {errorMsg && (
                <Typography color="#ef4444" fontSize={12} mt={2}>{errorMsg}</Typography>
            )}
        </div>
    )
}

export default ReferralSummary